import React, { useState, useEffect, useCallback } from 'react';
import {
  getEvents,
  checkInByQR, checkInBySearch, manualCheckIn,
  markNoShows, getAttendanceSummary,
  getNoShowThresholdBreaches,
  fmtDateTime,
} from '../../../api/eventsApi';

const TABS = [
  { key: 'qrCheckIn',    label: 'QR Check-In' },
  { key: 'searchManual', label: 'Search & Manual' },
  { key: 'noShows',      label: 'Mark No-Shows' },
  { key: 'summary',      label: 'Summary' },
];

const STATUS_BADGE = {
  CONFIRMED:  'bb-badge bb-badge--green',
  WAITLISTED: 'bb-badge bb-badge--orange',
  CANCELLED:  'bb-badge bb-badge--red',
  CHECKED_IN: 'bb-badge bb-badge--blue',
  NO_SHOW:    'bb-badge bb-badge--grey',
};

function TabBar({ activeTab, onSelect }) {
  return (
    <div className="bb-prog-tabs">
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onSelect(t.key)}
          className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function EventSelector({ events, value, onChange, label }) {
  return (
    <div className="bb-form-group" style={{ maxWidth: 460, marginTop: 24, marginBottom: 20 }}>
      <label style={{ paddingLeft: '3ch' }}>{label || 'Select Event'}</label>
      <select className="bb-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">-- Select an event --</option>
        {events.map(evt => (
          <option key={evt.id} value={evt.id}>{evt.title} ({fmtDateTime(evt.start_datetime)})</option>
        ))}
      </select>
    </div>
  );
}

export default function AttendanceCheckIn({ orgId, adminId }) {
  const [activeTab, setActiveTab] = useState('qrCheckIn');

  const [publishedEvents, setPublishedEvents] = useState([]);
  const [eventsLoading, setEventsLoading]     = useState(false);

  // QR Check-In
  const [qrEventId, setQrEventId]     = useState('');
  const [qrToken, setQrToken]         = useState('');
  const [qrResult, setQrResult]       = useState(null); // { success, member_name, error }
  const [qrCheckedInCount, setQrCheckedInCount] = useState(0);
  const [qrLoading, setQrLoading]     = useState(false);

  // Search & Manual
  const [searchEventId, setSearchEventId]   = useState('');
  const [searchTerm, setSearchTerm]         = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [searchLoading, setSearchLoading]   = useState(false);
  const [checkInLoading, setCheckInLoading] = useState({});

  // No-Shows
  const [noShowEventId, setNoShowEventId]         = useState('');
  const [showNoShowModal, setShowNoShowModal]     = useState(false);
  const [noShowLoading, setNoShowLoading]         = useState(false);
  const [noShowResult, setNoShowResult]           = useState(null);
  const [thresholdBreaches, setThresholdBreaches] = useState([]);
  const [breachesLoading, setBreachesLoading]     = useState(false);

  // Summary
  const [summaryEventId, setSummaryEventId]   = useState('');
  const [summary, setSummary]                 = useState(null);
  const [summaryLoading, setSummaryLoading]   = useState(false);

  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const showSuccessMsg = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const showErrorMsg   = (msg) => { setError(msg);   setTimeout(() => setError(''), 5000); };

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const all = await getEvents(orgId);
      setPublishedEvents(all.filter(e => e.status === 'PUBLISHED'));
    } catch (e) {
      showErrorMsg(e.message || 'Failed to load events.');
    } finally {
      setEventsLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Load no-show threshold breaches when tab opens
  useEffect(() => {
    if (activeTab === 'noShows') {
      setBreachesLoading(true);
      getNoShowThresholdBreaches(orgId, 3)
        .then(data => setThresholdBreaches(data || []))
        .catch(e => showErrorMsg(e.message || 'Failed to load breach data.'))
        .finally(() => setBreachesLoading(false));
    }
  }, [activeTab, orgId]);

  // ── QR Check-In ───────────────────────────────────────────────────────────────
  const handleQRSubmit = async (e) => {
    e.preventDefault();
    if (!qrEventId || !qrToken.trim()) return;
    setQrLoading(true);
    setQrResult(null);
    try {
      const result = await checkInByQR(orgId, qrEventId, qrToken.trim());
      setQrResult(result);
      if (result.success) {
        setQrCheckedInCount(p => p + 1);
        setQrToken('');
      }
    } catch (e) {
      setQrResult({ success: false, error: e.message || 'Check-in failed.' });
    } finally {
      setQrLoading(false);
    }
  };

  // ── Search & Manual ───────────────────────────────────────────────────────────
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchEventId || !searchTerm.trim()) return;
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const results = await checkInBySearch(orgId, searchEventId, searchTerm.trim());
      setSearchResults(results || []);
    } catch (e) {
      showErrorMsg(e.message || 'Search failed.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleManualCheckIn = async (reg) => {
    setCheckInLoading(p => ({ ...p, [reg.id]: true }));
    try {
      await manualCheckIn(orgId, searchEventId, reg.id, adminId, 'Manual override by admin');
      showSuccessMsg(`${reg.member_name} checked in.`);
      // Refresh results
      const results = await checkInBySearch(orgId, searchEventId, searchTerm.trim());
      setSearchResults(results || []);
    } catch (e) {
      showErrorMsg(e.message || 'Check-in failed.');
    } finally {
      setCheckInLoading(p => ({ ...p, [reg.id]: false }));
    }
  };

  // ── Mark No-Shows ─────────────────────────────────────────────────────────────
  const handleMarkNoShows = async () => {
    if (!noShowEventId) return;
    setNoShowLoading(true);
    try {
      const count = await markNoShows(orgId, noShowEventId, adminId);
      setNoShowResult(count);
      setShowNoShowModal(false);
      showSuccessMsg(`${count} member(s) marked as no-show.`);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to mark no-shows.');
    } finally {
      setNoShowLoading(false);
    }
  };

  // ── Summary ───────────────────────────────────────────────────────────────────
  const loadSummary = async (eventId) => {
    if (!eventId) return;
    setSummaryLoading(true);
    setSummary(null);
    try {
      const data = await getAttendanceSummary(orgId, eventId);
      setSummary(data);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to load summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const attendanceRate = summary
    ? summary.confirmed + summary.checked_in + summary.no_show > 0
      ? Math.round((summary.checked_in / (summary.confirmed + summary.checked_in + summary.no_show)) * 100)
      : 0
    : 0;

  const barTotal = summary ? Math.max(summary.confirmed + summary.checked_in + summary.waitlisted + summary.no_show + summary.cancelled, 1) : 1;

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <h2>Attendance &amp; Check-In</h2>
        <p>Manage check-ins, mark no-shows, and view attendance summaries.</p>
      </div>

      <TabBar activeTab={activeTab} onSelect={setActiveTab} />

      {error   && <div className="bb-alert bb-alert--error">{error}</div>}
      {success && <div className="bb-alert bb-alert--success">{success}</div>}

      {/* ── QR CHECK-IN ── */}
      {activeTab === 'qrCheckIn' && (
        <div style={{ maxWidth: 520 }}>
          {eventsLoading ? <div className="bb-loading-spinner" /> : (
            <EventSelector events={publishedEvents} value={qrEventId} onChange={setQrEventId} />
          )}

          {qrEventId && (
            <>
              <form onSubmit={handleQRSubmit}>
                <div className="bb-form-group">
                  <label style={{ fontSize: 16, fontWeight: 600, paddingLeft: '3ch' }}>Scan or Enter QR Token</label>
                  <input
                    className="bb-input"
                    value={qrToken}
                    onChange={e => setQrToken(e.target.value)}
                    placeholder="Scan QR or paste token here..."
                    autoFocus
                    style={{ fontSize: 16, padding: '12px 14px' }}
                  />
                </div>
                <button className="bb-btn-primary" type="submit" disabled={qrLoading || !qrToken.trim()}>
                  {qrLoading ? 'Checking in...' : 'Check In'}
                </button>
              </form>

              <div style={{ marginTop: 16, fontSize: 14, color: '#6B7280', paddingLeft: '3ch' }}>
                Checked in this session: <strong style={{ color: '#059669' }}>{qrCheckedInCount}</strong>
              </div>

              {qrResult && (
                <div style={{
                  marginTop: 20, padding: 20, borderRadius: 10, textAlign: 'center',
                  background: qrResult.success ? '#DCFCE7' : '#FEE2E2',
                  border: `1px solid ${qrResult.success ? '#86EFAC' : '#FCA5A5'}`,
                  color: qrResult.success ? '#166534' : '#991B1B',
                }}>
                  {qrResult.success ? (
                    <>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{qrResult.member_name}</div>
                      <div style={{ fontSize: 14, marginTop: 4 }}>Checked In!</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>✗</div>
                      <div style={{ fontWeight: 600 }}>{qrResult.error}</div>
                      {qrResult.member_name && <div style={{ fontSize: 13, marginTop: 4 }}>{qrResult.member_name}</div>}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── SEARCH & MANUAL ── */}
      {activeTab === 'searchManual' && (
        <div>
          {eventsLoading ? <div className="bb-loading-spinner" /> : (
            <EventSelector events={publishedEvents} value={searchEventId} onChange={setSearchEventId} />
          )}

          {searchEventId && (
            <>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <input
                  className="bb-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or ID..."
                  style={{ flex: 1 }}
                />
                <button className="bb-btn-primary" type="submit" disabled={searchLoading || !searchTerm.trim()}>
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </form>

              {searchResults.length === 0 && searchTerm && !searchLoading && (
                <div className="bb-empty-state" style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>No results found.</div>
              )}

              {searchResults.length > 0 && (
                <table className="bb-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(reg => (
                      <tr key={reg.id}>
                        <td><strong>{reg.member_name}</strong></td>
                        <td>{reg.member_email}</td>
                        <td><span className={STATUS_BADGE[reg.status] || 'bb-badge bb-badge--grey'}>{reg.status}</span></td>
                        <td>
                          {reg.status !== 'CHECKED_IN' && reg.status !== 'CANCELLED' && (
                            <button
                              className="bb-btn-sm"
                              style={{ background: '#059669', color: '#fff' }}
                              onClick={() => handleManualCheckIn(reg)}
                              disabled={checkInLoading[reg.id]}
                            >
                              {checkInLoading[reg.id] ? '...' : 'Check In'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {/* ── MARK NO-SHOWS ── */}
      {activeTab === 'noShows' && (
        <div>
          {eventsLoading ? <div className="bb-loading-spinner" /> : (
            <EventSelector events={publishedEvents} value={noShowEventId} onChange={setNoShowEventId} label="Select Past Event" />
          )}

          {noShowEventId && (
            <>
              <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>
                <strong>Warning:</strong> This will mark all CONFIRMED (not yet checked in) registrants as NO_SHOW for this event.
              </div>
              <button
                className="bb-btn-primary"
                style={{ background: '#DC2626' }}
                onClick={() => setShowNoShowModal(true)}
                disabled={noShowLoading}
              >
                Mark No-Shows
              </button>
            </>
          )}

          {noShowResult !== null && (
            <div className="bb-alert bb-alert--success" style={{ marginTop: 16 }}>
              {noShowResult} member(s) marked as no-show.
            </div>
          )}

          <div style={{ marginTop: 32 }}>
            <h3 style={{ marginBottom: 12, fontFamily: 'var(--bb-font-heading)', fontSize: 15, fontWeight: 700, color: '#0F766E', paddingLeft: '3ch' }}>No-Show Threshold Breaches (≥3)</h3>
            {breachesLoading ? (
              <div className="bb-loading-spinner" />
            ) : thresholdBreaches.length === 0 ? (
              <div className="bb-empty-state" style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>No members have breached the threshold.</div>
            ) : (
              <table className="bb-table">
                <thead>
                  <tr>
                    <th>Member ID</th>
                    <th>No-Show Count</th>
                    <th>No-Show Events</th>
                  </tr>
                </thead>
                <tbody>
                  {thresholdBreaches.map(b => (
                    <tr key={b.id}>
                      <td>{b.id}</td>
                      <td><strong style={{ color: '#DC2626' }}>{b.no_show_count}</strong></td>
                      <td style={{ fontSize: 12, color: '#6B7280' }}>{(b.no_show_events || []).length} events</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── SUMMARY ── */}
      {activeTab === 'summary' && (
        <div>
          {eventsLoading ? <div className="bb-loading-spinner" /> : (
            <EventSelector
              events={publishedEvents}
              value={summaryEventId}
              onChange={(id) => { setSummaryEventId(id); loadSummary(id); }}
            />
          )}

          {summaryLoading && <div className="bb-loading-spinner" />}

          {summary && !summaryLoading && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
                {[
                  { label: 'Confirmed',  value: summary.confirmed,  color: '#059669' },
                  { label: 'Checked In', value: summary.checked_in, color: '#2563EB' },
                  { label: 'Waitlisted', value: summary.waitlisted, color: '#D97706' },
                  { label: 'No-Show',    value: summary.no_show,    color: '#DC2626' },
                  { label: 'Cancelled',  value: summary.cancelled,  color: '#9CA3AF' },
                ].map(stat => (
                  <div key={stat.label} style={{
                    flex: '1 1 130px', minWidth: 120,
                    background: '#fff', border: '1px solid #E2E8F0',
                    borderRadius: 12, padding: '16px 20px', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                    <div style={{ fontSize: 12, color: '#64748B', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Attendance Rate: <span style={{ color: '#6B46C1' }}>{attendanceRate}%</span></div>
                <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', background: '#E5E7EB' }}>
                  {[
                    { val: summary.checked_in, color: '#2563EB' },
                    { val: summary.confirmed,  color: '#059669' },
                    { val: summary.waitlisted, color: '#D97706' },
                    { val: summary.no_show,    color: '#DC2626' },
                    { val: summary.cancelled,  color: '#D1D5DB' },
                  ].map((seg, i) => (
                    seg.val > 0 && (
                      <div key={i} style={{
                        width: `${(seg.val / barTotal) * 100}%`,
                        background: seg.color,
                        transition: 'width 0.4s ease',
                      }} title={`${seg.val}`} />
                    )
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap', fontSize: 12, color: '#6B7280' }}>
                  {[
                    { label: 'Checked In', color: '#2563EB' },
                    { label: 'Confirmed', color: '#059669' },
                    { label: 'Waitlisted', color: '#D97706' },
                    { label: 'No-Show', color: '#DC2626' },
                    { label: 'Cancelled', color: '#D1D5DB' },
                  ].map(l => (
                    <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: l.color, display: 'inline-block' }} />
                      {l.label}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── No-Show Confirmation Modal ── */}
      {showNoShowModal && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Confirm Mark No-Shows</h3>
            <p>All <strong>CONFIRMED</strong> registrants (not yet checked in) for this event will be marked as <strong>NO_SHOW</strong>. This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => setShowNoShowModal(false)}>Cancel</button>
              <button className="bb-btn-primary" style={{ background: '#DC2626' }} onClick={handleMarkNoShows} disabled={noShowLoading}>
                {noShowLoading ? 'Processing...' : 'Yes, Mark No-Shows'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
